import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Phone, Globe, MapPin, Linkedin, Twitter, Github, Instagram, Facebook, Youtube, Briefcase, Building, Share2, Download, ArrowLeft, Edit, Sparkles, Trash2, Eye, UserPlus, Send, BarChart3, MessageCircle, Crown, Lock, User, X, Navigation, FileText, Check, Copy, LayoutGrid, ChevronDown, ImageIcon, Calendar, Palette } from 'lucide-react';
import QRCode from 'react-qr-code';
import QRCodeWithLogo from '../components/QRCodeWithLogo';
import { cardAPI } from '../services/api';
import { useUserCard } from '../hooks/useUserCard';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import SEO from '../components/SEO';
import CustomizeCardModal from '../components/CustomizeCardModal';
import AnalyticsModal from '../components/AnalyticsModal';
import SignInModal from '../components/SignInModal';
import ScanLimitDialog from '../components/ScanLimitDialog';
import { generateAndDownloadVCard } from '../utils/vcardGenerator';
import BioTemplateSelector from '../components/bio-templates/BioTemplateSelector';
import BioTemplateRenderer from '../components/bio-templates/BioTemplateRenderer';
import BookDemoModal from '../components/BookDemoModal';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import CountryCodeSelect from '../components/CountryCodeSelect';
import QRPopupEnhanced from '../components/QRPopupEnhanced';
import { uploadImage } from '../firebase/config';

const Bio = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { userCard, loading: cardLoading } = useUserCard();
  const { isSubscribed } = useSubscriptionLimits();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  const [qrSize, setQrSize] = useState(145);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [contactExists, setContactExists] = useState(false);
  const trackingInProgressRef = useRef(false); // Prevent duplicate tracking
  const bioUrl = `${window.location.origin}/bio/${username}`; // Current bio page URL
  const cardUrl = `${window.location.origin}/bio/${username}`; // Bio page URL for sharing
  
  // Crop state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropType, setCropType] = useState(null); // 'profile' or 'logo'
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Sign-in modal state
  const [showSignInModal, setShowSignInModal] = useState(false);
  
  // Scan limit dialog state
  const [scanLimitDialog, setScanLimitDialog] = useState({
    open: false,
    scansUsed: 0,
    maxScans: 50,
    contactEmail: 'contact@allgrowtechnologies.com'
  });

  // Visitor registration state
  const [showVisitorModal, setShowVisitorModal] = useState(false);
  const [visitorName, setVisitorName] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [visitorCountryCode, setVisitorCountryCode] = useState('+91');
  const [visitorEmail, setVisitorEmail] = useState(''); // Optional email field
  const [visitorLocation, setVisitorLocation] = useState(''); // Display text for location
  const [visitorNotes, setVisitorNotes] = useState(''); // Notes field
  const [visitorCoords, setVisitorCoords] = useState(null); // {lat, lng} for GPS coordinates
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isSubmittingVisitor, setIsSubmittingVisitor] = useState(false);
  const [locationSource, setLocationSource] = useState(''); // 'gps' or 'ip' or ''

  // Email signature modal state
  const [showEmailSignatureModal, setShowEmailSignatureModal] = useState(false);
  const [signatureCopied, setSignatureCopied] = useState(false);
  const [selectedSignatureTemplate, setSelectedSignatureTemplate] = useState('classic');
  const [signatureQrUrl, setSignatureQrUrl] = useState(null);
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);

  // Meeting background modal state
  const [showMeetingBgModal, setShowMeetingBgModal] = useState(false);
  const [meetingBackgrounds, setMeetingBackgrounds] = useState([]);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [bgCategory, setBgCategory] = useState('all');
  const [isGeneratingBg, setIsGeneratingBg] = useState(false);

  // Book Demo modal state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [openSignatureFromUrl, setOpenSignatureFromUrl] = useState(false);
  const [openMeetingThemeFromUrl, setOpenMeetingThemeFromUrl] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Check for openSignature URL param on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openSignature') === 'true') {
      setOpenSignatureFromUrl(true);
    }
    if (urlParams.get('openMeetingTheme') === 'true') {
      setOpenMeetingThemeFromUrl(true);
    }
  }, []);

  // Template selector state for bottom panel
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [isChangingTemplate, setIsChangingTemplate] = useState(false);
  const templateDropdownRef = useRef(null);

  // Template list for dropdown
  const templatesList = [
    { id: 'classic', name: 'Classic' },
    { id: 'modern', name: 'Modern' },
    { id: 'minimal', name: 'Minimal' },
    { id: 'creative', name: 'Creative' },
    { id: 'professional', name: 'Professional' },
    { id: 'gradient', name: 'Gradient' },
    { id: 'executive', name: 'Executive' }
  ];

  // Swipe up gesture detection for QR popup
  const touchStartY = useRef(null);
  const touchEndY = useRef(null);
  const minSwipeDistance = 80; // Minimum swipe distance in pixels

  // Check if any popup/modal is open - disable swipe when popups are open
  const isAnyPopupOpen = showQRModal || customizeModalOpen || showAnalyticsModal || 
    showPremiumModal || showCropModal || showSignInModal || showVisitorModal || 
    showEmailSignatureModal || showMeetingBgModal || showDemoModal || showDeleteModal;

  // Swipe up gesture handler to open QR popup
  useEffect(() => {
    const handleTouchStart = (e) => {
      // Don't track swipes when any popup is open
      if (isAnyPopupOpen) return;
      
      touchEndY.current = null;
      touchStartY.current = e.targetTouches[0].clientY;
    };

    const handleTouchMove = (e) => {
      // Don't track swipes when any popup is open
      if (isAnyPopupOpen) return;
      
      touchEndY.current = e.targetTouches[0].clientY;
    };

    const handleTouchEnd = () => {
      // Don't process swipes when any popup is open
      if (isAnyPopupOpen) return;
      
      if (!touchStartY.current || !touchEndY.current) return;
      
      const distance = touchStartY.current - touchEndY.current;
      const isSwipeUp = distance > minSwipeDistance;
      
      // Check if swipe started from bottom 30% of screen
      const screenHeight = window.innerHeight;
      const swipeStartedFromBottom = touchStartY.current > screenHeight * 0.7;
      
      if (isSwipeUp && swipeStartedFromBottom && !showQRModal) {
        setShowQRModal(true);
      }
      
      // Reset
      touchStartY.current = null;
      touchEndY.current = null;
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [showQRModal, isAnyPopupOpen]);

  useEffect(() => {
    loadCard();
  }, [username]);

  // Check URL params to auto-open email signature modal
  useEffect(() => {
    if (card && !loading) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('openSignature') === 'true') {
        // Remove the param from URL to prevent re-opening on refresh
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        // Close visitor modal if it's open
        setShowVisitorModal(false);
        // Open the email signature modal
        setShowEmailSignatureModal(true);
      }
      if (urlParams.get('openMeetingTheme') === 'true') {
        // Remove the param from URL to prevent re-opening on refresh
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        // Close visitor modal if it's open
        setShowVisitorModal(false);
        
        // Get the selected background ID from URL
        const bgIdFromUrl = urlParams.get('bgId');
        
        // Load backgrounds and open the meeting background modal
        loadMeetingBackgrounds().then(() => {
          // Pre-select the background if bgId is provided
          if (bgIdFromUrl) {
            // Store the bgId to be selected after backgrounds load
            localStorage.setItem('pending_bg_selection', bgIdFromUrl);
          }
        });
        setShowMeetingBgModal(true);
      }
    }
  }, [card, loading]);

  
  // Update favicon dynamically when card loads
  useEffect(() => {
    const userImage = card?.profileImage || card?.avatar;
    if (userImage) {
      // Update all favicon links
      const updateFavicon = (selector, href) => {
        let link = document.querySelector(selector);
        if (link) {
          link.href = href;
        } else {
          link = document.createElement('link');
          link.rel = selector.includes('apple') ? 'apple-touch-icon' : 'icon';
          if (selector.includes('32x32')) {
            link.sizes = '32x32';
          }
          link.type = 'image/png';
          link.href = href;
          document.head.appendChild(link);
        }
      };
      
      updateFavicon('link[rel="icon"]:not([sizes])', userImage);
      updateFavicon('link[rel="icon"][sizes="32x32"]', userImage);
      updateFavicon('link[rel="apple-touch-icon"]', userImage);
    }
    
    // Cleanup: restore default favicon when component unmounts
    return () => {
      const restoreFavicon = (selector, defaultHref) => {
        const link = document.querySelector(selector);
        if (link) {
          link.href = defaultHref;
        }
      };
      
      restoreFavicon('link[rel="icon"]:not([sizes])', '/logo.png');
      restoreFavicon('link[rel="icon"][sizes="32x32"]', '/logo.png');
      restoreFavicon('link[rel="apple-touch-icon"]', '/logo.png');
    };
  }, [card?.profileImage, card?.avatar]);

  // Track view analytics (only if not admin and not card owner)
  useEffect(() => {
    const trackBioView = async () => {
      // Wait for admin check to complete before tracking
      if (!adminCheckComplete) {
        console.log('⏳ [Bio] Waiting for admin check to complete...');
        return;
      }
      
      // Check if tracking is already in progress
      if (trackingInProgressRef.current) {
        console.log('🚫 [Bio] Tracking already in progress, skipping...');
        return;
      }
      
      if (card && username && (card._id || card.id)) {
        const cardId = card._id || card.id;
        
        // CRITICAL: Check if user is admin - BLOCK IMMEDIATELY
        if (isAdmin) {
          console.log('🚫 [Bio] ADMIN USER DETECTED - Skipping all analytics tracking');
          return;
        }
        
        // Don't track if user is viewing their own card
        if (user && card.userId === user.uid) {
          console.log('⏭️ [Bio] Skipping analytics tracking for card owner');
          return;
        }
        
        // Set flag IMMEDIATELY before async operations
        trackingInProgressRef.current = true;
        
        // Track the view (count every page load/refresh)
        try {
          const { analyticsAPI } = await import('../services/api');
          
          // Try to get approximate location from IP using BigDataCloud (CORS-friendly)
          let locationInfo = { city: '', region: '', country: '' };
          try {
            const geoResponse = await fetch('https://api.bigdatacloud.net/data/reverse-geocode-client');
            if (geoResponse.ok) {
              const geoData = await geoResponse.json();
              locationInfo = {
                city: geoData.city || geoData.locality || '',
                region: geoData.principalSubdivision || '',
                country: geoData.countryName || '',
                ip: null
              };
            }
          } catch (geoError) {
            console.log('📍 [Bio] Could not get location, using default');
          }
          
          // Prepare viewer info with location
          const viewerInfo = {
            userAgent: navigator.userAgent,
            deviceType: /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            referrer: document.referrer || 'direct',
            url: window.location.href,
            timestamp: new Date().toISOString(),
            viewerId: user?.uid || null,
            viewerName: user?.displayName || null,
            viewerEmail: user?.email || null,
            isAdminUser: false, // Explicitly mark as non-admin
            location: locationInfo
          };
          
          await analyticsAPI.trackView(cardId, username, viewerInfo);
          
          console.log('✅ [Bio] View tracked successfully for non-admin user');
        } catch (error) {
          console.error('❌ [Bio] Error tracking view:', error);
        } finally {
          // Reset flag after tracking completes
          setTimeout(() => {
            trackingInProgressRef.current = false;
          }, 1000);
        }
      }
    };

    trackBioView();
  }, [card, username, user, isAdmin, adminCheckComplete]);

  // Check if contact already exists for this user
  useEffect(() => {
    const checkContactExists = async () => {
      if (user && card) {
        try {
          const isDigitalCard = card.username && card.userId;
          
          if (isDigitalCard) {
            // Check relationship for digital cards
            const { contactRelationshipAPI } = await import('../services/api');
            const result = await contactRelationshipAPI.checkExists(card.username);
            setContactExists(result.exists);
          } else if (card._id) {
            // Check old contacts for physical cards
            const { contactAPI } = await import('../services/api');
            const result = await contactAPI.checkExists(user.uid, card._id);
            setContactExists(result.exists);
          }
        } catch (error) {
          console.error('Error checking contact:', error);
        }
      }
    };

    checkContactExists();
  }, [user, card]);

  // Set QR code size based on screen width
  useEffect(() => {
    const updateQRSize = () => {
      if (window.innerWidth < 640) {
        setQrSize(115); // Mobile size - slightly reduced
      } else {
        setQrSize(145); // Desktop size - increased by 5px
      }
    };

    // Set initial size
    updateQRSize();

    // Add resize listener
    window.addEventListener('resize', updateQRSize);

    // Cleanup
    return () => window.removeEventListener('resize', updateQRSize);
  }, []);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      setAdminCheckComplete(false); // Mark as checking
      
      if (!user) {
        setIsAdmin(false);
        setAdminCheckComplete(true);
        return;
      }

      // Check by email first
      const adminEmails = ["sagarshiva111555@gmail.com", "allgrowtechonologies@gmail.com"];
      if (adminEmails.includes(user.email)) {
        setIsAdmin(true);
        setAdminCheckComplete(true);
        return;
      }

      // Also check custom claims if available
      if (user.getIdTokenResult) {
        try {
          const tokenResult = await user.getIdTokenResult();
          setIsAdmin(tokenResult.claims.admin === true);
        } catch (error) {
          console.log('Could not check admin claims:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      
      setAdminCheckComplete(true); // Mark check as complete
    };

    checkAdmin();
  }, [user]);

  const loadCard = async () => {
    try {
      const data = await cardAPI.getByUsername(username);
      setCard(data);
    } catch (error) {
      console.error('Error loading card:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show visitor registration modal for every visitor (not card owner, not admin)
  useEffect(() => {
    if (!card || loading || !adminCheckComplete) return;
    
    // Don't show for logged-in users who are the card owner
    if (user && card.userId === user.uid) return;
    
    // Don't show for admin users
    if (isAdmin) return;
    
    // Don't show visitor modal if openSignature or openMeetingTheme URL param was set
    if (openSignatureFromUrl || openMeetingThemeFromUrl) return;
    
    // Show visitor modal after a short delay on every visit
    const timer = setTimeout(async () => {
      // Prefill info for logged-in users
      if (user) {
        let prefillName = user.displayName || '';
        let prefillPhone = '';
        let prefillCountryCode = '+91';
        
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const token = await user.getIdToken();
          
          // First try to get from user profile
          try {
            const profileRes = await fetch(`${API_URL}/api/users/profile`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (profileRes.ok) {
              const profile = await profileRes.json();
              if (profile.displayName && !prefillName) prefillName = profile.displayName;
              if (profile.name && !prefillName) prefillName = profile.name;
              // Check multiple phone field names
              const phoneValue = profile.phoneNumber || profile.phone;
              if (phoneValue) {
                prefillPhone = phoneValue.toString().trim();
                if (profile.countryCode) prefillCountryCode = profile.countryCode;
              }
            }
          } catch (e) {
            console.log('Profile fetch failed, trying cards');
          }
          
          // If no phone from profile, try from user's first card
          if (!prefillPhone) {
            const cardsRes = await fetch(`${API_URL}/api/cards/user/${user.uid}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (cardsRes.ok) {
              const userCards = await cardsRes.json();
              const userCard = Array.isArray(userCards) ? userCards[0] : userCards;
              
              if (userCard) {
                // Set name from card if not already set
                if (!prefillName && userCard.name) {
                  prefillName = userCard.name;
                }
                
                // Set phone from card
                if (userCard.phone) {
                  const phone = userCard.phone.toString().trim();
                  // Check if phone already has country code
                  if (phone.startsWith('+')) {
                    const codes = ['+91', '+1', '+44', '+61', '+86', '+81', '+49', '+33', '+971', '+65', '+60', '+966', '+92', '+880', '+977'];
                    const matchedCode = codes.find(c => phone.startsWith(c));
                    if (matchedCode) {
                      prefillCountryCode = matchedCode;
                      prefillPhone = phone.slice(matchedCode.length).replace(/^\s+/, '');
                    } else {
                      prefillPhone = phone.replace(/^\+/, '');
                    }
                  } else {
                    prefillPhone = phone;
                  }
                }
              }
            }
          }
        } catch (e) {
          console.log('Could not fetch user data for prefill:', e);
        }
        
        // Apply prefilled values
        if (prefillName) setVisitorName(prefillName);
        if (prefillPhone) {
          setVisitorPhone(prefillPhone);
          setVisitorCountryCode(prefillCountryCode);
        }
      }
      
      setShowVisitorModal(true);
      // Auto-fetch approximate location from IP
      fetchApproximateLocation();
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [card, loading, adminCheckComplete, user, isAdmin, openSignatureFromUrl, openMeetingThemeFromUrl]);

  // Fetch approximate location from IP (no permission needed)
  const fetchApproximateLocation = async () => {
    setIsLoadingLocation(true);
    try {
      // Use BigDataCloud for IP geolocation (CORS-friendly, free)
      let locationText = '';
      
      try {
        const response = await fetch('https://api.bigdatacloud.net/data/reverse-geocode-client');
        if (response.ok) {
          const data = await response.json();
          const parts = [data.locality || data.city, data.countryName].filter(Boolean);
          if (parts.length > 0) {
            locationText = parts.join(', ');
          }
        }
      } catch (e) {
        console.log('BigDataCloud IP location failed:', e);
      }
      
      setVisitorLocation(locationText);
      if (locationText) setLocationSource('ip');
    } catch (e) {
      console.error('Location fetch error:', e);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Get precise GPS location (requires user permission)
  const getGPSLocation = async () => {
    if (!navigator.geolocation) {
      toast({ title: 'Not supported', description: 'GPS is not available on this device', variant: 'destructive' });
      return;
    }
    
    setIsLoadingLocation(true);
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        });
      });
      
      const { latitude, longitude, accuracy } = position.coords;
      console.log(`GPS coordinates: ${latitude}, ${longitude} (accuracy: ${accuracy}m)`);
      
      // Store the exact coordinates
      setVisitorCoords({ lat: latitude, lng: longitude });
      
      // Get simplified address for display: Area, City, Country
      let displayAddress = '';
      
      try {
        // Use BigDataCloud for clean area/city/country
        const response = await fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
        );
        
        if (response.ok) {
          const data = await response.json();
          
          // Build simple address: Locality/Area, City, Country
          const parts = [
            data.locality || data.city,
            data.city !== data.locality ? data.city : null,
            data.countryName
          ].filter(Boolean);
          
          displayAddress = parts.join(', ');
        }
      } catch (e) {
        console.log('BigDataCloud failed:', e);
      }
      
      // Fallback to Nominatim if needed
      if (!displayAddress) {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=14&addressdetails=1`,
            { headers: { 'User-Agent': 'PiperCard/1.0' } }
          );
          
          if (response.ok) {
            const data = await response.json();
            const addr = data.address || {};
            const parts = [
              addr.suburb || addr.neighbourhood || addr.locality,
              addr.city || addr.town || addr.village,
              addr.country
            ].filter(Boolean);
            displayAddress = parts.join(', ');
          }
        } catch (e) {
          console.log('Nominatim also failed:', e);
        }
      }
      
      // Final fallback
      if (!displayAddress) {
        displayAddress = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      }
      
      setVisitorLocation(displayAddress);
      setLocationSource('gps');
      toast({ title: 'Location captured', description: `Accuracy: ~${Math.round(accuracy)}m` });
      
    } catch (error) {
      console.error('GPS error:', error);
      if (error.code === 1) {
        toast({ title: 'Permission denied', description: 'Please allow location access to share your location', variant: 'destructive' });
      } else if (error.code === 2) {
        toast({ title: 'Location unavailable', description: 'Could not determine your location', variant: 'destructive' });
      } else {
        toast({ title: 'Timeout', description: 'Location request timed out. Please try again', variant: 'destructive' });
      }
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Validate phone number
  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 6 && digits.length <= 15;
  };

  // Submit visitor registration
  const submitVisitorRegistration = async () => {
    // Validate inputs
    if (!visitorName || visitorName.trim().length < 2) {
      toast({ title: 'Invalid name', description: 'Please enter your name (at least 2 characters)', variant: 'destructive' });
      return;
    }
    
    if (!validatePhone(visitorPhone)) {
      toast({ title: 'Invalid phone', description: 'Please enter a valid phone number (6-15 digits)', variant: 'destructive' });
      return;
    }
    
    setIsSubmittingVisitor(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const cardId = card._id || card.id;
      
      // Parse location - store GPS coordinates if available
      const locationParts = visitorLocation ? visitorLocation.split(',').map(s => s.trim()).filter(Boolean) : [];
      const locationData = visitorLocation && visitorLocation.trim() ? {
        type: locationSource || 'manual',
        address: visitorLocation.trim(),
        city: locationParts[0] || '',
        region: locationParts[1] || '',
        country: locationParts[2] || '',
        lat: visitorCoords?.lat || null,
        lng: visitorCoords?.lng || null
      } : null;
      
      const response = await fetch(`${API_URL}/api/analytics/register-visitor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardId,
          cardUsername: username,
          name: visitorName.trim(),
          phone: visitorPhone,
          countryCode: visitorCountryCode,
          email: visitorEmail.trim() || null, // Include optional email
          location: locationData,
          notes: visitorNotes.trim() || null // Include notes
        })
      });
      
      if (response.ok) {
        setShowVisitorModal(false);
        toast({ title: 'Welcome!', description: `Thanks for visiting, ${visitorName}!` });
        
        // Reset form
        setVisitorName('');
        setVisitorPhone('');
        setVisitorEmail('');
        setVisitorLocation('');
        setVisitorNotes('');
        setVisitorCoords(null);
        setLocationSource('');
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to register', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Visitor registration error:', e);
      toast({ title: 'Error', description: 'Failed to register. Please try again.', variant: 'destructive' });
    } finally {
      setIsSubmittingVisitor(false);
    }
  };

  // Skip visitor registration
  const skipVisitorRegistration = () => {
    setShowVisitorModal(false);
    // Reset form
    setVisitorName('');
    setVisitorPhone('');
    setVisitorEmail('');
    setVisitorLocation('');
    setVisitorNotes('');
    setVisitorCoords(null);
    setLocationSource('');
  };

  // Track link click (exclude admin users and card owners)
  const trackLinkClick = async (linkType, linkValue) => {
    try {
      // Don't track if user is viewing their own card
      if (user && card.userId === user.uid) {
        console.log('⏭️ [Bio] Skipping link click tracking for card owner');
        return;
      }
      
      // Check if user is admin - CRITICAL: Do real-time API check for accuracy
      const { adminAPI } = await import('../services/api');
      if (user) {
        try {
          const adminStatus = await adminAPI.checkAdminStatus();
          if (adminStatus.isAdmin) {
            console.log('🚫 [Bio] ADMIN USER DETECTED - Skipping link click tracking');
            return;
          }
        } catch (error) {
          // If admin check fails, also check isAdmin state as fallback
          if (isAdmin) {
            console.log('🚫 [Bio] ADMIN USER DETECTED (fallback) - Skipping link click tracking');
            return;
          }
          console.log('[Bio] Admin check API failed, continuing with tracking (non-admin assumed)');
        }
      }
      
      // Track the link click
      const { analyticsAPI } = await import('../services/api');
      const cardId = card._id || card.id;
      const cardUsername = card.username;
      
      if (cardId && cardUsername) {
        // Include viewer info for backend validation
        const viewerInfo = {
          viewerId: user?.uid || null,
          viewerEmail: user?.email || null,
          viewerName: user?.displayName || null,
          isAdminUser: false, // Explicitly mark as non-admin (already checked above)
          timestamp: new Date().toISOString()
        };
        
        await analyticsAPI.trackLinkClick(cardId, cardUsername, linkType, linkValue, viewerInfo);
        console.log(`✅ [Bio] Tracked ${linkType} click for non-admin user`);
      }
    } catch (error) {
      console.error('❌ [Bio] Error tracking link click:', error);
    }
  };

  const handleAddContact = async () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to add this contact.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Check if this is a digital card (has username) or physical card
      const isDigitalCard = card.username && card.userId;
      
      if (isDigitalCard) {
        // Use relationship API for digital cards (creates bidirectional connection + notification)
        const { contactRelationshipAPI } = await import('../services/api');
        await contactRelationshipAPI.create(card.username);
        
        toast({
          title: "Connected!",
          description: `You and ${card.name} are now connected. They've been notified.`
        });
      } else {
        // Use old contact API for physical cards or cards without userIds (one-way)
        const { cachedContactAPI } = await import('../services/cachedApi');
        
        // Create contact data from the card
        const contactData = {
          userId: user.uid, // Current user who is saving the contact
          name: card.name,
          email: card.email,
          phone: card.phone,
          company: card.company,
          title: card.title,
          website: card.website || (card.websiteName && card.websiteTLD ? `${card.websiteName}${card.websiteTLD}` : null),
          location: card.location,
          bio: card.bio,
          profileImage: card.profileImage,
          cardId: card.id || card._id,
          cardUsername: card.username,
          linkedin: card.linkedin,
          twitter: card.twitter,
          github: card.github,
          instagram: card.instagram,
          youtube: card.youtube,
          facebook: card.facebook,
          whatsapp: card.whatsapp,
          notes: '' // Initialize with empty notes
        };

        await cachedContactAPI.createContact(contactData, user.uid);
        
        toast({
          title: "Contact added!",
          description: `${card.name} has been added to your contacts.`
        });
      }
      
      // Update contactExists state
      setContactExists(true);
    } catch (error) {
      console.error('Error adding contact:', error);
      
      // Handle scan limit error (402 Payment Required)
      if (error.response?.status === 402) {
        const errorDetail = error.response?.data?.detail;
        setScanLimitDialog({
          open: true,
          scansUsed: errorDetail?.scans_used || 50,
          maxScans: errorDetail?.max_scans || 50,
          contactEmail: errorDetail?.contact_email || 'contact@allgrowtechnologies.com'
        });
        return;
      }
      
      // Handle specific error messages
      const errorMessage = error.response?.data?.detail || "Failed to add contact. Please try again.";
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleShare = async () => {
    try {
      // Try to use native share API
      if (navigator.share) {
        await navigator.share({
          title: `${card.name}'s Digital Bio`,
          text: `Check out ${card.name}'s digital bio`,
          url: bioUrl
        });
        toast({
          title: "Shared successfully!",
          description: "Bio link has been shared."
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(bioUrl);
        toast({
          title: "Link copied!",
          description: "Bio link copied to clipboard."
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // User cancelled or error occurred, fallback to copying link
        try {
          await navigator.clipboard.writeText(bioUrl);
          toast({
            title: "Link copied!",
            description: "Bio link copied to clipboard."
          });
        } catch (clipboardError) {
          console.error('Error copying to clipboard:', clipboardError);
          toast({
            title: "Error",
            description: "Failed to share or copy link. Please try again.",
            variant: "destructive"
          });
        }
      }
    }
  };

  const handleShareQR = async () => {
    try {
      const qrElement = document.getElementById('qr-code-share');
      const canvas = await import('html2canvas').then(module => module.default);
      const qrCanvas = await canvas(qrElement, {
        backgroundColor: null,
        scale: 2
      });

      qrCanvas.toBlob(async (blob) => {
        const file = new File([blob], `${card.name}_QR.png`, { type: 'image/png' });
        
        if (navigator.share && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              title: `${card.name}'s QR Code`,
              text: `Scan to view ${card.name}'s bio: ${bioUrl}`,
              files: [file]
            });
          } catch (error) {
            console.error('Error sharing QR:', error);
          }
        } else {
          // Fallback: Download the QR code
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${card.name}_QR.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          alert('QR Code downloaded!');
        }
      });
    } catch (error) {
      console.error('Error sharing QR code:', error);
      alert('Failed to share QR code');
    }
  };

  const handleDownloadVCard = async () => {
    try {
      // Check if mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        toast({
          title: "Opening Contacts...",
          description: "Your contacts app will open to save this contact."
        });
      } else {
        toast({
          title: "Preparing contact...",
          description: "Generating contact file with all information."
        });
      }

      // Track the contact save FIRST (don't wait for vCard success)
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const cardId = card._id || card.id;
      
      // Fire and forget - don't block vCard download
      fetch(`${API_URL}/api/analytics/contact-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardId: typeof cardId === 'object' ? cardId.toString() : cardId,
          cardUsername: card.username,
          viewerInfo: {
            deviceType: isMobile ? 'mobile' : 'desktop',
            userAgent: navigator.userAgent,
            referrer: document.referrer || 'direct'
          }
        })
      }).then(res => {
        if (res.ok) console.log('✅ Contact save tracked');
        else console.error('❌ Track failed:', res.status);
      }).catch(err => console.error('❌ Track error:', err));

      // Generate and download/share vCard with photo and all details
      const success = await generateAndDownloadVCard(card, true);

      if (success) {
        // Small delay to let the contacts app open
        setTimeout(() => {
          toast({
            title: isMobile ? "Contact Ready!" : "Contact Downloaded!",
            description: isMobile 
              ? "Your contacts app should open. Tap 'Add' or 'Done' to save." 
              : "Import the downloaded file to your contacts app."
          });
        }, isMobile ? 500 : 100);
      }
    } catch (error) {
      console.error('Error generating vCard:', error);
      toast({
        title: "Contact File Created",
        description: "Please check your downloads or contacts app.",
        variant: "default"
      });
    }
  };

  const handleAddToGoogleContacts = async () => {
    try {
      toast({
        title: "Opening Google Contacts...",
        description: "You'll be asked to sign in to Google."
      });

      // Google OAuth configuration
      const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '377738193167-2c77qrlub6qv8q3hh99sml6j7itd3v89.apps.googleusercontent.com';
      const REDIRECT_URI = window.location.origin + '/google-callback';
      const SCOPE = 'https://www.googleapis.com/auth/contacts';

      // Create contact data to pass to callback
      const contactData = {
        name: card.name,
        givenName: card.name?.split(' ')[0] || '',
        familyName: card.name?.split(' ').slice(1).join(' ') || '',
        email: card.email,
        phone: card.phone,
        phone2: card.phone2,
        company: card.company,
        title: card.title,
        website: card.website,
        websiteName: card.websiteName,
        tld: card.tld,
        location: card.location,
        locationAddress: card.locationAddress,
        bio: card.bio,
        profileImage: card.profileImage,
        linkedin: card.linkedin,
        twitter: card.twitter,
        github: card.github,
        instagram: card.instagram,
        facebook: card.facebook,
        youtube: card.youtube,
        whatsapp: card.whatsapp
      };

      // Store contact data in localStorage (persists across popup windows)
      localStorage.setItem('pendingGoogleContact', JSON.stringify(contactData));
      console.log('Stored contact data in localStorage:', contactData);

      // Build Google OAuth URL with state parameter
      const state = Date.now().toString(); // Simple state for CSRF protection
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}` +
        `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
        `&response_type=token` +
        `&scope=${encodeURIComponent(SCOPE)}` +
        `&state=${state}` +
        `&include_granted_scopes=true` +
        `&prompt=consent`;

      // Open OAuth popup
      const width = 500;
      const height = 600;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      const popup = window.open(
        authUrl,
        'Google Sign In',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for message from popup
      const messageHandler = (event) => {
        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          window.removeEventListener('message', messageHandler);
          toast({
            title: "Contact Added!",
            description: "Successfully added to your Google Contacts."
          });
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          window.removeEventListener('message', messageHandler);
          toast({
            title: "Error",
            description: event.data.error || "Failed to add contact to Google.",
            variant: "destructive"
          });
        }
      };

      window.addEventListener('message', messageHandler);

      // Cleanup if popup is closed
      const checkPopup = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageHandler);
        }
      }, 1000);

    } catch (error) {
      console.error('Error adding to Google Contacts:', error);
      toast({
        title: "Error",
        description: "Failed to open Google Contacts. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
      return;
    }

    try {
      await cardAPI.delete(username);
      
      toast({
        title: "Card Deleted",
        description: "Your card has been successfully deleted."
      });
      
      // Redirect to home page after deletion
      navigate('/');
    } catch (error) {
      console.error('Error deleting card:', error);
      toast({
        title: "Error",
        description: "Failed to delete card. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Generate and upload QR code to Firebase for email signature
  const generateAndUploadQrCode = async () => {
    // Generate QR even if user is not owner (for viewing others' cards)
    if (!card) return null;
    
    // Check if card already has a signature QR URL stored
    if (card.signatureQrUrl) {
      setSignatureQrUrl(card.signatureQrUrl);
      return card.signatureQrUrl;
    }
    
    setIsGeneratingQr(true);
    
    try {
      const isPremium = card?.is_premium_card || isSubscribed;
      const defaultFreeColor = '#a89743';
      const defaultPremiumColor = '#f59e0b';
      const brandColor = isPremium 
        ? (card.qrColor || card.customization?.primaryColor || defaultPremiumColor)
        : defaultFreeColor;
      const qrColorHex = brandColor.replace('#', '');
      
      // Simple QR code URL without centerImageUrl (which causes 400 errors)
      const qrApiUrl = `https://quickchart.io/qr?text=${encodeURIComponent(bioUrl)}&size=300&dark=${qrColorHex}&light=ffffff&margin=1&format=png`;
      
      // If user is authenticated and is the card owner, upload to Firebase
      if (user && (card.userId === user.uid || isOwner)) {
        // Fetch QR code image
        const response = await fetch(qrApiUrl);
        if (!response.ok) throw new Error('Failed to fetch QR code');
        
        const blob = await response.blob();
        
        // Upload to Firebase
        const storagePath = `signature-qr/${card.userId || user.uid}/${card.username || username}_qr.png`;
        const firebaseUrl = await uploadImage(blob, storagePath);
        
        // Save the URL to the card in database
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const token = await user.getIdToken();
          await fetch(`${API_URL}/api/cards/${card.username || username}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ signatureQrUrl: firebaseUrl })
          });
        } catch (e) {
          console.log('Could not save QR URL to card, but continuing...');
        }
        
        setSignatureQrUrl(firebaseUrl);
        return firebaseUrl;
      } else {
        // Non-owner or not authenticated - use direct QuickChart URL
        setSignatureQrUrl(qrApiUrl);
        return qrApiUrl;
      }
      
    } catch (error) {
      console.error('Error generating QR code:', error);
      // Fallback to simple QR code without logo
      const isPremium = card?.is_premium_card || isSubscribed;
      const defaultFreeColor = '#a89743';
      const defaultPremiumColor = '#f59e0b';
      const brandColor = isPremium 
        ? (card?.qrColor || card?.customization?.primaryColor || defaultPremiumColor)
        : defaultFreeColor;
      const qrColorHex = brandColor.replace('#', '');
      const fallbackUrl = `https://quickchart.io/qr?text=${encodeURIComponent(bioUrl)}&size=200&dark=${qrColorHex}&light=ffffff&margin=1&format=png`;
      setSignatureQrUrl(fallbackUrl);
      return fallbackUrl;
    } finally {
      setIsGeneratingQr(false);
    }
  };

  // Generate Email Signature HTML - Multiple HiHello-style templates
  const generateEmailSignatureHTML = (template = selectedSignatureTemplate, qrUrl = null) => {
    if (!card) return '';
    
    const profileImage = card.profileImage || card.avatar;
    const companyLogo = card.companyLogo || card.logo;
    const name = card.name || 'Your Name';
    const title = card.title || '';
    const company = card.company || '';
    const email = card.email || '';
    const phone = card.phone || '';
    const website = card.website || '';
    const linkedinRaw = card.linkedin || '';
    const twitter = card.twitter || '';
    const instagram = card.instagram || '';
    
    // Construct LinkedIn URL - handle both profile and company pages
    const getLinkedInUrl = (id) => {
      if (!id) return '';
      if (id.startsWith('http://') || id.startsWith('https://')) return id;
      if (id.includes('/company/') || id.includes('company/')) {
        const companyName = id.replace(/.*company\//i, '').replace(/\/$/, '');
        return `https://linkedin.com/company/${companyName}`;
      }
      if (id.includes('/in/')) {
        const username = id.replace(/.*\/in\//i, '').replace(/\/$/, '');
        return `https://linkedin.com/in/${username}`;
      }
      return `https://linkedin.com/in/${id}`;
    };
    const linkedin = getLinkedInUrl(linkedinRaw);
    
    const isPremium = card?.is_premium_card || isSubscribed;
    
    // Default QR color: #a89743 for free users, premium users can have custom color
    const defaultFreeColor = '#a89743';
    const defaultPremiumColor = '#f59e0b';
    // Free users always get the default free color, premium users get their custom color or default premium
    const brandColor = isPremium 
      ? (card.qrColor || card.customization?.primaryColor || defaultPremiumColor)
      : defaultFreeColor;
    const qrColorHex = brandColor.replace('#', '');
    
    // Use production URL for logo (publicly accessible for QR API)
    const piperLogoUrl = 'https://pipercard.in/piper-logo-qr.png';
    // For premium users with company logo, use their logo; otherwise use Piper Card logo
    const logoToShow = isPremium && companyLogo ? companyLogo : piperLogoUrl;
    
    // Use Firebase-stored QR URL if available, otherwise fallback to QuickChart (without centerImageUrl which causes errors)
    const fallbackQrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(bioUrl)}&size=300&dark=${qrColorHex}&light=ffffff&margin=1`;
    const qrCodeUrl = qrUrl || signatureQrUrl || card.signatureQrUrl || fallbackQrUrl;
    
    // Simple QR component - logo is already embedded in the image
    const qrWithLogo = (size) => `
      <a href="${bioUrl}" style="text-decoration: none; display: inline-block;">
        <img src="${qrCodeUrl}" width="${size}" height="${size}" style="display: block; border-radius: 12px;" alt="QR Code">
      </a>`;
    
    // Piper Card branding for free users - simple text only
    const piperBranding = !isPremium ? `
  <tr>
    <td colspan="3" style="padding-top: 12px;">
      <a href="https://pipercard.in" style="font-size: 10px; color: #999; text-decoration: none; font-family: Arial, sans-serif;">Made with <span style="color: #f59e0b; font-weight: 600;">Piper Card</span></a>
    </td>
  </tr>` : '';

    // Template 1: Classic - Clean professional layout (LARGER)
    if (template === 'classic') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          ${profileImage ? `<td style="vertical-align: top; padding-right: 16px;">
            <img src="${profileImage}" width="90" height="90" style="border-radius: 12px; display: block; object-fit: cover;" alt="${name}">
          </td>` : ''}
          <td style="vertical-align: top; padding-right: 16px;">
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #1a1a1a;">${name}</p>
            ${title ? `<p style="margin: 3px 0 0 0; font-size: 14px; color: #555;">${title}</p>` : ''}
            ${company ? `<p style="margin: 2px 0 0 0; font-size: 14px; color: #777;">${company}</p>` : ''}
            <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 10px; font-size: 13px; color: #444;">
              ${phone ? `<tr><td style="padding: 2px 0;"><a href="tel:${phone}" style="color: #444; text-decoration: none;">${phone}</a></td></tr>` : ''}
              ${email ? `<tr><td style="padding: 2px 0;"><a href="mailto:${email}" style="color: #444; text-decoration: none;">${email}</a></td></tr>` : ''}
            </table>
          </td>
          <td style="vertical-align: top;">
            ${qrWithLogo(90)}
          </td>
        </tr>
      </table>
      ${piperBranding}
    </td>
  </tr>
</table>`.trim();
    }

    // Template 2: Overlay - HiHello style with text overlay on image, QR on right (LARGER)
    if (template === 'modern') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          ${profileImage ? `<td style="vertical-align: top; padding-right: 16px;">
            <img src="${profileImage}" width="90" height="90" style="border-radius: 12px; display: block; object-fit: cover;" alt="${name}">
          </td>` : ''}
          <td style="vertical-align: top; padding-right: 16px;">
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #1a1a1a;">${name}</p>
            ${title ? `<p style="margin: 3px 0 0 0; font-size: 13px; color: #555;">${title}</p>` : ''}
            ${company ? `<p style="margin: 2px 0 0 0; font-size: 13px; color: #777;">${company}</p>` : ''}
            <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 8px; font-size: 12px; color: #555;">
              ${phone ? `<tr><td style="padding: 2px 0;">${phone}</td></tr>` : ''}
              ${email ? `<tr><td style="padding: 2px 0;">${email}</td></tr>` : ''}
            </table>
          </td>
          <td style="vertical-align: top;">
            <table cellpadding="0" cellspacing="0" border="0" style="background: ${brandColor}; border-radius: 12px; padding: 8px;">
              <tr><td>
                ${qrWithLogo(80)}
              </td></tr>
            </table>
          </td>
        </tr>
      </table>
      ${piperBranding}
    </td>
  </tr>
</table>`.trim();
    }

    // Template 3: Minimal - Ultra clean text only (LARGER)
    if (template === 'minimal') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="vertical-align: middle; padding-right: 16px;">
            <p style="margin: 0; font-size: 17px; font-weight: 600; color: #1a1a1a;">${name}</p>
            ${title || company ? `<p style="margin: 4px 0 0 0; font-size: 13px; color: #666;">${title}${title && company ? ' · ' : ''}${company}</p>` : ''}
            <p style="margin: 8px 0 0 0; font-size: 12px; color: #888;">
              ${phone ? `<span>${phone}</span>` : ''}${phone && email ? ' · ' : ''}${email ? `<span>${email}</span>` : ''}
            </p>
          </td>
          <td style="vertical-align: middle;">
            ${qrWithLogo(80)}
          </td>
        </tr>
      </table>
      ${piperBranding}
    </td>
  </tr>
</table>`.trim();
    }

    // Template 4: Executive - Premium with image text overlay (LARGER)
    if (template === 'compact') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="background: #ffffff; border: 1px solid #e0e0e0; border-radius: 16px; padding: 20px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          ${profileImage ? `<td style="vertical-align: top; padding-right: 16px;">
            <img src="${profileImage}" width="90" height="90" style="border-radius: 12px; display: block; object-fit: cover;" alt="${name}">
          </td>` : ''}
          <td style="vertical-align: middle; padding-right: 16px;">
            <p style="margin: 0; font-size: 16px; font-weight: 600; color: #1a1a1a;">${name}</p>
            ${title ? `<p style="margin: 2px 0 0 0; font-size: 13px; color: #555;">${title}</p>` : ''}
            ${company ? `<p style="margin: 2px 0 0 0; font-size: 13px; color: #777;">${company}</p>` : ''}
            <p style="margin: 6px 0 0 0; font-size: 12px; color: #888;">${phone || ''}${phone && email ? ' · ' : ''}${email || ''}</p>
          </td>
          <td style="vertical-align: middle;">
            ${qrWithLogo(90)}
          </td>
        </tr>
      </table>
      ${piperBranding}
    </td>
  </tr>
</table>`.trim();
    }

    // Template 5: Banner - Full width colored header style
    if (template === 'banner') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif;">
  <tr>
    <td style="border: 1px solid #e0e0e0; border-radius: 16px; overflow: hidden;">
      <table cellpadding="0" cellspacing="0" border="0" width="400">
        <tr>
          <td style="background: ${brandColor}; padding: 16px 20px;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                ${profileImage ? `<td style="width: 60px; vertical-align: middle;">
                  <img src="${profileImage}" width="55" height="55" style="border-radius: 50%; border: 3px solid white; display: block; object-fit: cover;" alt="${name}">
                </td>` : ''}
                <td style="vertical-align: middle; padding-left: ${profileImage ? '14px' : '0'};">
                  <p style="margin: 0; font-size: 18px; font-weight: 700; color: white;">${name}</p>
                  ${title ? `<p style="margin: 3px 0 0 0; font-size: 12px; color: rgba(255,255,255,0.9);">${title}</p>` : ''}
                  ${company ? `<p style="margin: 2px 0 0 0; font-size: 12px; color: rgba(255,255,255,0.85);">${company}</p>` : ''}
                </td>
                <td style="width: 75px; text-align: right; vertical-align: middle;">
                  <a href="${bioUrl}" style="text-decoration: none;">
                    <img src="${qrCodeUrl}" width="65" height="65" style="display: block; border-radius: 6px; border: 2px solid white;" alt="QR">
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="background: white; padding: 12px 20px;">
            <table cellpadding="0" cellspacing="0" border="0" style="font-size: 12px; color: #555;">
              <tr>
                ${phone ? `<td style="padding-right: 20px;"><a href="tel:${phone}" style="color: #555; text-decoration: none;">${phone}</a></td>` : ''}
                ${email ? `<td style="padding-right: 20px;"><a href="mailto:${email}" style="color: #555; text-decoration: none;">${email}</a></td>` : ''}
                ${website ? `<td><a href="${website.startsWith('http') ? website : 'https://' + website}" style="color: ${brandColor}; text-decoration: none; font-weight: 500;">${website.replace(/^https?:\/\//, '')}</a></td>` : ''}
              </tr>
            </table>
          </td>
        </tr>
        ${!isPremium ? `<tr>
          <td style="background: #f9f9f9; padding: 8px 20px; border-top: 1px solid #eee;">
            <a href="https://pipercard.in" style="font-size: 10px; color: #999; text-decoration: none;">Made with <span style="color: #f59e0b; font-weight: 600;">Piper Card</span></a>
          </td>
        </tr>` : ''}
      </table>
    </td>
  </tr>
</table>`.trim();
    }

    return '';
  };

  // Copy email signature - renders HTML and copies as rich text for email clients
  const copyEmailSignature = async () => {
    try {
      // First, generate QR code (will work even without authentication now)
      let qrUrl = signatureQrUrl || card?.signatureQrUrl;
      if (!qrUrl) {
        toast({
          title: "Generating QR Code...",
          description: "Please wait while we create your signature."
        });
        qrUrl = await generateAndUploadQrCode();
      }
      
      // Generate HTML with the QR URL
      const html = generateEmailSignatureHTML(selectedSignatureTemplate, qrUrl);
    
      // Create a temporary container to render the HTML
      const container = document.createElement('div');
      container.innerHTML = html;
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      document.body.appendChild(container);
      
      // Select the rendered content
      const range = document.createRange();
      range.selectNodeContents(container);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Copy as rich text
      document.execCommand('copy');
      
      // Cleanup
      selection.removeAllRanges();
      document.body.removeChild(container);
      
      setSignatureCopied(true);
      toast({
        title: "Signature Copied!",
        description: "Paste directly into Gmail or Outlook signature settings."
      });
      
      setTimeout(() => setSignatureCopied(false), 3000);
    } catch (error) {
      console.error('Error copying signature:', error);
      toast({
        title: "Copy Failed",
        description: "Please try again or copy manually.",
        variant: "destructive"
      });
    }
  };

  // Load meeting backgrounds
  const loadMeetingBackgrounds = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Fetch default backgrounds
      const response = await fetch(`${API_URL}/api/backgrounds`);
      let backgrounds = [];
      if (response.ok) {
        backgrounds = await response.json();
      }
      
      // Fetch user's custom backgrounds if logged in
      if (user) {
        try {
          const token = await user.getIdToken();
          const userBgResponse = await fetch(`${API_URL}/api/backgrounds/my-backgrounds`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (userBgResponse.ok) {
            const userBackgrounds = await userBgResponse.json();
            // Add user backgrounds at the beginning
            backgrounds = [...userBackgrounds, ...backgrounds];
          }
        } catch (e) {
          console.log('Could not load user backgrounds');
        }
      }
      
      setMeetingBackgrounds(backgrounds);
      
      // Check if there's a pending background selection from URL
      const pendingBgId = localStorage.getItem('pending_bg_selection');
      if (pendingBgId) {
        const matchingBg = backgrounds.find(bg => bg.id === pendingBgId);
        if (matchingBg) {
          setSelectedBackground(matchingBg);
        } else if (backgrounds.length > 0) {
          setSelectedBackground(backgrounds[0]);
        }
        localStorage.removeItem('pending_bg_selection');
      } else if (backgrounds.length > 0 && !selectedBackground) {
        setSelectedBackground(backgrounds[0]);
      }
    } catch (error) {
      console.error('Error loading backgrounds:', error);
    }
  };

  // Upload user background
  const uploadUserBackground = async (file) => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/backgrounds/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Background Uploaded!",
          description: "Your custom background is ready to use."
        });
        // Reload backgrounds
        loadMeetingBackgrounds();
        return data;
      } else {
        const error = await response.json();
        toast({
          title: "Upload Failed",
          description: error.detail || "Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error uploading background:', error);
      toast({
        title: "Upload Failed",
        description: "Please try again.",
        variant: "destructive"
      });
    }
  };

  // Delete user background
  const deleteUserBackground = async (bgId) => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/backgrounds/my-backgrounds/${bgId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({
          title: "Background Deleted",
          description: "Your background has been removed."
        });
        // Reload backgrounds and clear selection if needed
        if (selectedBackground?.id === bgId) {
          setSelectedBackground(null);
        }
        loadMeetingBackgrounds();
      }
    } catch (error) {
      console.error('Error deleting background:', error);
    }
  };

  // Delete card permanently
  const handleDeleteCard = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete') {
      toast({
        title: "Confirmation Required",
        description: "Please type 'delete' to confirm.",
        variant: "destructive"
      });
      return;
    }
    
    setIsDeleting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/cards/${username}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({
          title: "Card Deleted",
          description: "Your digital business card has been permanently deleted."
        });
        // Redirect to home or card builder
        navigate('/');
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.detail || "Failed to delete card. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      toast({
        title: "Error",
        description: "Failed to delete card. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }
  };

  // Generate and download meeting background
  const generateMeetingBackground = async () => {
    if (!selectedBackground || !card) return;
    
    setIsGeneratingBg(true);
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Virtual background size (16:9 aspect ratio for video calls)
      canvas.width = 1920;
      canvas.height = 1080;
      
      // Draw background
      if (selectedBackground.type === 'gradient') {
        // Parse gradient and draw
        const gradient = selectedBackground.gradient;
        // Extract colors from CSS gradient
        const colorMatch = gradient.match(/#[a-fA-F0-9]{6}/g);
        if (colorMatch && colorMatch.length >= 2) {
          const grd = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
          grd.addColorStop(0, colorMatch[0]);
          grd.addColorStop(1, colorMatch[1]);
          ctx.fillStyle = grd;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      } else if (selectedBackground.type === 'url' && selectedBackground.image_url) {
        // Load from external URL (Unsplash)
        try {
          const bgImg = await loadImage(selectedBackground.image_url);
          ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
        } catch (e) {
          console.log('Failed to load URL image, using fallback gradient');
          const grd = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
          grd.addColorStop(0, '#667eea');
          grd.addColorStop(1, '#764ba2');
          ctx.fillStyle = grd;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      } else if (selectedBackground.thumbnail_url) {
        // Load and draw image background from API
        const imgUrl = selectedBackground.thumbnail_url.startsWith('http') 
          ? selectedBackground.thumbnail_url 
          : `${process.env.REACT_APP_BACKEND_URL}${selectedBackground.thumbnail_url}`;
        const bgImg = await loadImage(imgUrl);
        ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
      }
      
      // Add subtle overlay for text readability
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // === TOP LEFT: Card Info Panel with photo, name, designation, company (DOUBLED SIZE) ===
      const panelWidth = 640;
      const panelHeight = 200;
      const panelX = 50;
      const panelY = 50;
      
      // Draw glass panel background with shadow effect
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 8;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.beginPath();
      ctx.roundRect(panelX, panelY, panelWidth, panelHeight, 20);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      
      // Profile image (if available)
      const profileImage = card.profileImage || card.avatar;
      let textStartX = panelX + 30;
      let imageLoaded = false;
      
      if (profileImage) {
        try {
          const img = await loadImageForCanvas(profileImage);
          if (img) {
            const imgSize = 140;
            const imgX = panelX + 30;
            const imgY = panelY + (panelHeight - imgSize) / 2;
            
            // Circular clip for profile image
            ctx.save();
            ctx.beginPath();
            ctx.arc(imgX + imgSize/2, imgY + imgSize/2, imgSize/2, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(img, imgX, imgY, imgSize, imgSize);
            ctx.restore();
            
            // Add border to profile image
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(imgX + imgSize/2, imgY + imgSize/2, imgSize/2, 0, Math.PI * 2);
            ctx.stroke();
            
            textStartX = imgX + imgSize + 25;
            imageLoaded = true;
          }
        } catch (e) {
          console.log('Could not load profile image:', e);
        }
      }
      
      // If no image loaded but profile exists, draw a placeholder circle
      if (!imageLoaded && profileImage) {
        const imgSize = 140;
        const imgX = panelX + 30;
        const imgY = panelY + (panelHeight - imgSize) / 2;
        
        // Draw placeholder circle
        ctx.fillStyle = '#e5e7eb';
        ctx.beginPath();
        ctx.arc(imgX + imgSize/2, imgY + imgSize/2, imgSize/2, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw initials
        ctx.fillStyle = '#6b7280';
        ctx.font = 'bold 48px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initials = (card.name || 'U').charAt(0).toUpperCase();
        ctx.fillText(initials, imgX + imgSize/2, imgY + imgSize/2);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
        
        textStartX = imgX + imgSize + 25;
      }
      
      // Draw text - Name, Designation, and Company (DOUBLED FONT SIZES)
      const name = card.name || 'Your Name';
      const title = card.title || '';
      const company = card.company || '';
      
      // Name
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 40px Arial, sans-serif';
      ctx.fillText(name, textStartX, panelY + 70);
      
      // Title/Designation
      if (title) {
        ctx.fillStyle = '#555';
        ctx.font = '28px Arial, sans-serif';
        ctx.fillText(title, textStartX, panelY + 115);
      }
      
      // Company name
      if (company) {
        ctx.fillStyle = '#777';
        ctx.font = 'italic 26px Arial, sans-serif';
        ctx.fillText(company, textStartX, panelY + 155);
      }
      
      // === TOP RIGHT: Large QR Code Panel ===
      const qrPanelSize = 352;
      const qrPanelX = canvas.width - qrPanelSize - 50;
      const qrPanelY = 35;
      
      // Draw QR panel background with rounded corners
      ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 10;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
      ctx.beginPath();
      ctx.roundRect(qrPanelX, qrPanelY, qrPanelSize, qrPanelSize, 24);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      
      // QR Code (fills the panel with small padding)
      const qrPadding = 25;
      const qrSize = qrPanelSize - (qrPadding * 2);
      const qrX = qrPanelX + qrPadding;
      const qrY = qrPanelY + qrPadding;
      
      // Get brand color for QR
      const isPremium = card?.is_premium_card || isSubscribed;
      const qrColor = isPremium 
        ? (card.qrColor || '#7c3aed').replace('#', '')
        : '7c3aed';
      
      // Generate QR code - premium users get logo, free users get simple QR
      let qrUrl;
      if (isPremium) {
        const piperLogoUrl = 'https://pipercard.in/piper-logo-qr.png';
        const logoToShow = card.companyLogo || piperLogoUrl;
        qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(bioUrl)}&size=600&dark=${qrColor}&light=ffffff&centerImageUrl=${encodeURIComponent(logoToShow)}&centerImageSizeRatio=0.28&margin=1`;
      } else {
        // Free users - simple QR without logo (more reliable)
        qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(bioUrl)}&size=600&dark=${qrColor}&light=ffffff&margin=2`;
      }
      
      try {
        const qrImg = await loadImage(qrUrl);
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
      } catch (e) {
        // Fallback QR without logo
        const fallbackQrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(bioUrl)}&size=600&dark=${qrColor}&light=ffffff&margin=2`;
        try {
          const qrImg = await loadImage(fallbackQrUrl);
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
        } catch (e2) {
          console.log('Could not load QR code');
          // Draw placeholder text if QR fails completely
          ctx.fillStyle = '#999';
          ctx.font = '16px Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('QR Code', qrX + qrSize/2, qrY + qrSize/2);
          ctx.textAlign = 'left';
        }
      }
      
      // Add "Created by pipercard.com" watermark for free users
      if (!isPremium) {
        const watermarkText = 'Created by pipercard.com';
        ctx.font = '18px Arial, sans-serif';
        const textWidth = ctx.measureText(watermarkText).width;
        
        // Position at bottom center
        const watermarkX = (canvas.width - textWidth) / 2;
        const watermarkY = canvas.height - 30;
        
        // Draw subtle background pill
        ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.beginPath();
        ctx.roundRect(watermarkX - 20, watermarkY - 18, textWidth + 40, 32, 16);
        ctx.fill();
        
        // Draw text
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fillText(watermarkText, watermarkX, watermarkY);
      }
      
      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${card.name || 'meeting'}_background.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast({
          title: "Background Downloaded!",
          description: "Set this as your virtual background in Zoom, Google Meet, or Teams."
        });
      }, 'image/png');
      
    } catch (error) {
      console.error('Error generating background:', error);
      toast({
        title: "Error",
        description: "Failed to generate background. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingBg(false);
    }
  };

  // Helper to load image as promise with CORS handling
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => {
        // Try without crossOrigin for same-origin images
        const img2 = new Image();
        img2.onload = () => resolve(img2);
        img2.onerror = reject;
        img2.src = src;
      };
      img.src = src;
    });
  };

  // Helper to load image for canvas - uses proxy for external images that may have CORS issues
  const loadImageForCanvas = async (src) => {
    if (!src) return null;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Base64 data URLs work directly without CORS issues
    if (src.startsWith('data:image')) {
      try {
        const img = await loadImage(src);
        return img;
      } catch (e) {
        console.log('Base64 image load failed');
        return null;
      }
    }
    
    // Check if this is an external URL that needs proxying
    const needsProxy = src && (
      src.includes('firebasestorage.googleapis.com') ||
      src.includes('storage.googleapis.com') ||
      src.includes('googleusercontent.com') ||
      src.includes('lh3.googleusercontent.com') ||
      // Proxy any HTTPS URL that isn't from our own domain
      (src.startsWith('https://') && !src.includes(window.location.hostname))
    );
    
    // Use proxy URL for external images
    const imageUrl = needsProxy 
      ? `${API_URL}/api/backgrounds/proxy-image?url=${encodeURIComponent(src)}`
      : src;
    
    try {
      const img = await loadImage(imageUrl);
      return img;
    } catch (e) {
      console.log('Image load failed for:', src, '- trying without proxy');
      // Fallback: try loading without proxy
      try {
        const img = await loadImage(src);
        return img;
      } catch (e2) {
        console.log('Image load failed completely for:', src);
        return null;
      }
    }
  };

  const handleSaveCustomization = async (customizationData) => {
    try {
      await cardAPI.update(username, customizationData);
      
      // Invalidate cache to ensure customization updates immediately
      try {
        const cacheManager = (await import('../utils/cache')).default;
        cacheManager.clearAll();
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
      
      // Reload card data to show updated customization
      const updatedCard = await cardAPI.getByUsername(username);
      setCard(updatedCard);
      
      toast({
        title: "Success",
        description: "Card customization updated successfully"
      });
    } catch (error) {
      console.error('Error saving customization:', error);
      toast({
        title: "Error",
        description: "Failed to update customization",
        variant: "destructive"
      });
      throw error;
    }
  };

  // Crop callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Helper to create image element
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  // Create cropped image
  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          resolve(reader.result);
        };
      }, 'image/jpeg', 0.95);
    });
  };

  const handleUpdateProfileImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please select an image file.",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image smaller than 15MB.",
        variant: "destructive"
      });
      return;
    }

    // Read file and show crop modal
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropImageSrc(e.target.result);
      setCropType('profile');
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateLogo = async (event) => {
    // Check for card premium OR account subscription - only free users are restricted
    const hasPremium = card?.is_premium_card || isSubscribed;
    if (!hasPremium) {
      setShowPremiumModal(true);
      // Reset the file input if it exists
      if (event?.target?.value !== undefined) {
        event.target.value = '';
      }
      return;
    }

    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please select an image file.",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image smaller than 15MB.",
        variant: "destructive"
      });
      return;
    }

    // Read file and show crop modal
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropImageSrc(e.target.result);
      setCropType('logo');
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    if (!croppedAreaPixels || !cropImageSrc) return;

    try {
      // Get cropped image
      const croppedImage = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      
      // Update based on type
      const updateData = cropType === 'profile' 
        ? { profileImage: croppedImage }
        : { companyLogo: croppedImage };

      await cardAPI.update(username, updateData);
      
      // Reload card data
      const updatedCard = await cardAPI.getByUsername(username);
      setCard(updatedCard);
      
      // Close modal and reset
      setShowCropModal(false);
      setCropImageSrc(null);
      setCropType(null);
      
      toast({
        title: "Success",
        description: `${cropType === 'profile' ? 'Profile image' : 'Logo'} updated successfully`
      });
    } catch (error) {
      console.error('Error updating image:', error);
      toast({
        title: "Error",
        description: "Failed to update image",
        variant: "destructive"
      });
    }
  };

  // Handle gallery image upload from bio tabs
  const handleUploadGalleryImages = async (files) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to upload images.",
        variant: "destructive"
      });
      return;
    }
    
    if (!card) {
      toast({
        title: "Error",
        description: "Card data not loaded.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const token = await user.getIdToken();
      const currentImages = card.galleryImages || [];
      const maxImages = 4;
      const remainingSlots = maxImages - currentImages.length;
      
      if (remainingSlots <= 0) {
        toast({
          title: "Gallery Full",
          description: "Maximum 4 images allowed",
          variant: "destructive"
        });
        return;
      }

      const filesToUpload = files.slice(0, remainingSlots);
      const newImageUrls = [];

      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cards/gallery/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          newImageUrls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
        }
      }

      if (newImageUrls.length > 0) {
        const updatedImages = [...currentImages, ...newImageUrls];
        await cardAPI.update(username, { galleryImages: updatedImages });
        
        // Reload card data
        const updatedCard = await cardAPI.getByUsername(username);
        setCard(updatedCard);
        
        toast({
          title: "Success",
          description: `${newImageUrls.length} image(s) uploaded successfully`
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: "Error",
        description: "Failed to upload images",
        variant: "destructive"
      });
    }
  };

  // Handle adding YouTube video from bio tabs
  const handleAddVideo = async (videoId) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to add videos.",
        variant: "destructive"
      });
      return;
    }
    
    if (!card) {
      toast({
        title: "Error",
        description: "Card data not loaded.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const currentVideos = card.youtubeVideos || [];
      const maxVideos = 3;
      
      if (currentVideos.length >= maxVideos) {
        toast({
          title: "Video Limit Reached",
          description: "Maximum 3 videos allowed",
          variant: "destructive"
        });
        return;
      }

      if (currentVideos.includes(videoId)) {
        toast({
          title: "Duplicate Video",
          description: "This video is already added",
          variant: "destructive"
        });
        return;
      }

      const updatedVideos = [...currentVideos, videoId];
      await cardAPI.update(username, { youtubeVideos: updatedVideos });
      
      // Reload card data
      const updatedCard = await cardAPI.getByUsername(username);
      setCard(updatedCard);
      
      toast({
        title: "Success",
        description: "Video added successfully"
      });
    } catch (error) {
      console.error('Error adding video:', error);
      toast({
        title: "Error",
        description: "Failed to add video",
        variant: "destructive"
      });
    }
  };

  // Handle deleting gallery image from bio tabs
  const handleDeleteImage = async (index) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to delete images.",
        variant: "destructive"
      });
      return;
    }
    
    if (!card) {
      toast({
        title: "Error",
        description: "Card data not loaded.",
        variant: "destructive"
      });
      return;
    }

    try {
      const currentImages = card.galleryImages || [];
      if (index < 0 || index >= currentImages.length) {
        return;
      }

      const updatedImages = currentImages.filter((_, i) => i !== index);
      await cardAPI.update(username, { galleryImages: updatedImages });
      
      // Reload card data
      const updatedCard = await cardAPI.getByUsername(username);
      setCard(updatedCard);
      
      toast({
        title: "Success",
        description: "Image deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive"
      });
    }
  };

  // Handle deleting YouTube video from bio tabs
  const handleDeleteVideo = async (index) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to delete videos.",
        variant: "destructive"
      });
      return;
    }
    
    if (!card) {
      toast({
        title: "Error",
        description: "Card data not loaded.",
        variant: "destructive"
      });
      return;
    }

    try {
      const currentVideos = card.youtubeVideos || [];
      if (index < 0 || index >= currentVideos.length) {
        return;
      }

      const updatedVideos = currentVideos.filter((_, i) => i !== index);
      await cardAPI.update(username, { youtubeVideos: updatedVideos });
      
      // Reload card data
      const updatedCard = await cardAPI.getByUsername(username);
      setCard(updatedCard);
      
      toast({
        title: "Success",
        description: "Video deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting video:', error);
      toast({
        title: "Error",
        description: "Failed to delete video",
        variant: "destructive"
      });
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropImageSrc(null);
    setCropType(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Handle template change from bottom panel
  const handleTemplateChange = async (templateId, templateName) => {
    if (!user || isChangingTemplate) return;
    
    setIsChangingTemplate(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cards/${username}/template`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ template: templateId })
      });

      if (!response.ok) {
        throw new Error('Failed to update template');
      }

      // Update local state
      setCard({ ...card, bioTemplate: templateId });
      setShowTemplateDropdown(false);
      
      toast({
        title: 'Template Updated!',
        description: `Your bio page now uses the ${templateName} template.`
      });
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        title: 'Error',
        description: 'Failed to update template. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsChangingTemplate(false);
    }
  };

  // Close template dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target)) {
        setShowTemplateDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const constructSocialUrl = (platform, id) => {
    if (!id) return null;
    
    // Handle LinkedIn - can be profile URL, company URL, or just username
    if (platform === 'linkedin') {
      // If it's already a full URL, return as-is
      if (id.startsWith('http://') || id.startsWith('https://')) {
        return id;
      }
      // If it contains /company/, it's a company page
      if (id.includes('/company/') || id.includes('company/')) {
        const companyName = id.replace(/.*company\//i, '').replace(/\/$/, '');
        return `https://linkedin.com/company/${companyName}`;
      }
      // If it contains /in/, it's a profile
      if (id.includes('/in/')) {
        const username = id.replace(/.*\/in\//i, '').replace(/\/$/, '');
        return `https://linkedin.com/in/${username}`;
      }
      // Default to profile URL
      return `https://linkedin.com/in/${id}`;
    }
    
    const urls = {
      twitter: `https://twitter.com/${id}`,
      github: `https://github.com/${id}`,
      instagram: `https://instagram.com/${id}`,
      facebook: `https://facebook.com/${id}`,
      youtube: id.startsWith('@') ? `https://youtube.com/${id}` : `https://youtube.com/@${id}`,
      whatsapp: `https://wa.me/${id.replace(/[^0-9]/g, '')}`
    };
    return urls[platform];
  };

  const constructWebsiteUrl = (name, tld) => {
    if (!name || !tld) return null;
    return `https://${name}${tld}`;
  };

  if (loading) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!card) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Card Not Found</h1>
            <p className="text-gray-600 mb-8">The card you&apos;re looking for doesn&apos;t exist.</p>
            <Link to="/">
              <Button className="bg-blue-600 hover:bg-blue-700">Go Home</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const fontClass = {
    arial: 'font-sans',
    playfair: 'font-serif',
    cinzel: 'font-serif',
    cormorant: 'font-serif'
  }[card.fontFamily] || 'font-sans';

  // Generate structured data for person/bio page
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": card.name,
    "url": `https://pipercard.com/bio/${card.username}`,
    "image": card.profileImage || card.avatar,
    "jobTitle": card.title,
    "description": card.bio || `Digital business card for ${card.name}`,
    "worksFor": card.company ? {
      "@type": "Organization",
      "name": card.company
    } : undefined,
    "email": card.email,
    "telephone": card.phone,
    "address": card.location,
    "sameAs": [
      card.linkedin,
      card.twitter,
      card.github,
      card.facebook,
      card.instagram,
      card.youtube,
      card.whatsapp
    ].filter(Boolean)
  };

  const bioDescription = card.bio 
    ? `${card.bio.substring(0, 150)}... Connect with ${card.name} on Piper Card.`
    : `Connect with ${card.name}${card.title ? ` - ${card.title}` : ''}${card.company ? ` at ${card.company}` : ''}. View digital business card with contact info and social links.`;

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <SEO 
        title={`${card.name}${card.title ? ` - ${card.title}` : ''} | Digital Business Card`}
        description={bioDescription}
        image={card.profileImage || card.avatar}
        type="profile"
        canonicalUrl={`https://pipercard.com/bio/${card.username}`}
        structuredData={personSchema}
        keywords={`${card.name} business card, ${card.name} contact, digital business card, ${card.company || 'professional'}, ${card.title || 'contact'}`}
        favicon={card.profileImage || card.avatar}
      />

      <div className="pb-24">
        <div className="w-full relative pl-0 lg:pl-4">
          
          {/* Template Selector - Only visible to card owner */}
          {user && card.userId === user.uid && (
            <BioTemplateSelector
              currentTemplate={card.bioTemplate || 'classic'}
              onTemplateChange={(newTemplate) => {
                // Update local card state
                setCard({ ...card, bioTemplate: newTemplate });
              }}
              username={username}
              card={{
                ...card,
                onUploadImages: handleUploadGalleryImages,
                onAddVideo: handleAddVideo,
                onDeleteImage: handleDeleteImage,
                onDeleteVideo: handleDeleteVideo
              }}
              bioUrl={bioUrl}
              constructSocialUrl={constructSocialUrl}
              trackLinkClick={trackLinkClick}
              onUpdateProfileImage={handleUpdateProfileImage}
              onUpdateLogo={handleUpdateLogo}
              isPremium={card?.is_premium_card || isSubscribed}
            />
          )}

          {/* Render Selected Bio Template */}
          {user && card.userId === user.uid ? (
            // Owner view - only hidden bio card for sharing/wallpaper capture (template selector shows the visible one)
            <div id="bio-card-share" className="relative hidden">
              <BioTemplateRenderer
                card={card}
                bioUrl={bioUrl}
                constructSocialUrl={constructSocialUrl}
                trackLinkClick={trackLinkClick}
                onQRClick={() => setShowQRModal(true)}
                hideQR={true}
                isOwner={false}
                isPremium={card?.is_premium_card || isSubscribed}
              />
            </div>
          ) : (
            // Visitor view - visible bio card (QR shown on screen, but hidden in downloads)
            <>
              <div id="bio-card-share" className="relative">
                <BioTemplateRenderer
                  card={card}
                  bioUrl={bioUrl}
                  constructSocialUrl={constructSocialUrl}
                  trackLinkClick={trackLinkClick}
                  onQRClick={() => setShowQRModal(true)}
                  hideQR={true}
                  isOwner={false}
                  isPremium={card?.is_premium_card || isSubscribed}
                />
              </div>
              {/* Hidden card for download capture - QR hidden */}
              <div id="bio-card-download" className="relative hidden">
                <BioTemplateRenderer
                  card={card}
                  bioUrl={bioUrl}
                  constructSocialUrl={constructSocialUrl}
                  trackLinkClick={() => {}}
                  onQRClick={() => {}}
                  hideQR={true}
                  isOwner={false}
                  isPremium={card?.is_premium_card || isSubscribed}
                />
              </div>
              
              {/* NFC Card Promotion - Show only if bio doesn't have NFC card linked */}
              {!card.nfc_code && (
                <div className="mt-6 mx-auto max-w-md">
                  <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-5 border border-amber-500/30 shadow-xl">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
                          <Sparkles className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-lg mb-1">Get Your NFC Card</h3>
                        <p className="text-gray-400 text-sm">Share your contact with a single tap. Premium NFC business cards.</p>
                      </div>
                    </div>
                    <Link to="/shop">
                      <Button className="w-full mt-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold py-3 rounded-xl shadow-lg">
                        Order Your NFC Card
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}

        </div>

      </div>

      {/* Fixed Button at Bottom - For All Users */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl p-3 sm:p-4 z-50">
        {/* Swipe Up Indicator - Mobile Only */}
        <div 
          className="sm:hidden flex justify-center mb-2 cursor-pointer"
          onClick={() => setShowQRModal(true)}
          data-testid="swipe-up-indicator"
        >
          <div className="flex flex-col items-center text-gray-400 animate-bounce">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            <span className="text-xs">Swipe up for QR</span>
          </div>
        </div>
        <div className="max-w-md mx-auto">
          {/* Main Action Buttons */}
          <div className="space-y-3">
            {/* Secondary Actions based on user type */}
            {user && card.userId === user.uid ? (
              // Card owner - Template selector, Edit, Meeting BG, Email Signature, Share
              <div className="space-y-2">
                {/* Row 1: Theme, Edit, Delete - Equal size */}
                <div className="grid grid-cols-3 gap-2">
                  {/* Template Dropdown */}
                  <div className="relative" ref={templateDropdownRef}>
                    <button
                      onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                      className="w-full h-full flex items-center justify-center gap-1.5 bg-white hover:bg-gray-50 border border-gray-200 px-2 py-2.5 rounded-lg transition-all shadow-sm"
                      data-testid="template-dropdown-btn"
                    >
                      <LayoutGrid className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Theme</span>
                      <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${showTemplateDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown Menu - Grid Layout */}
                    {showTemplateDropdown && (
                      <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[60]">
                        <div className="p-2">
                          <div className="grid grid-cols-2 gap-1.5">
                            {templatesList.map((template) => {
                              const isActive = template.id === (card.bioTemplate || 'classic');
                              return (
                                <button
                                  key={template.id}
                                  onClick={() => {
                                    if (!isActive) {
                                      handleTemplateChange(template.id, template.name);
                                    } else {
                                      setShowTemplateDropdown(false);
                                    }
                                  }}
                                  disabled={isChangingTemplate}
                                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all ${
                                    isActive 
                                      ? 'bg-amber-100 text-amber-700 border border-amber-300' 
                                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-transparent'
                                  } ${isChangingTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  data-testid={`template-option-${template.id}`}
                                >
                                  <span className="text-xs font-medium">{template.name}</span>
                                  {isActive && <Check className="w-3.5 h-3.5 text-amber-600" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Edit Button */}
                  <Button
                    onClick={() => navigate(`/edit/${username}`)}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2.5 rounded-lg shadow-sm"
                    data-testid="edit-card-btn"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    <span className="text-sm font-medium">Edit</span>
                  </Button>
                  
                  {/* Delete Button */}
                  <Button
                    onClick={() => setShowDeleteModal(true)}
                    variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 py-2.5 rounded-lg"
                    data-testid="delete-card-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    <span className="text-sm font-medium">Delete</span>
                  </Button>
                </div>

                {/* Row 2: Edit Bio, Meeting BG, Email Signature */}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => navigate(`/bio/${username}/edit`)}
                    variant="outline"
                    className="w-full border border-purple-200 text-purple-600 hover:bg-purple-50 py-2 rounded-lg text-xs"
                    data-testid="edit-bio-btn"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" />
                    <span>Edit Bio</span>
                  </Button>
                  <Button
                    onClick={() => {
                      loadMeetingBackgrounds();
                      setShowMeetingBgModal(true);
                    }}
                    variant="outline"
                    className="w-full border border-gray-200 text-gray-600 hover:bg-gray-50 py-2 rounded-lg text-xs"
                    data-testid="meeting-bg-btn"
                  >
                    <Palette className="w-3.5 h-3.5 mr-1" />
                    <span>Background</span>
                  </Button>
                  <Button
                    onClick={() => {
                      setShowEmailSignatureModal(true);
                      if (!signatureQrUrl && !card?.signatureQrUrl) {
                        generateAndUploadQrCode();
                      }
                    }}
                    variant="outline"
                    className="w-full border border-gray-200 text-gray-600 hover:bg-gray-50 py-2 rounded-lg text-xs"
                    data-testid="email-signature-btn"
                  >
                    <Mail className="w-3.5 h-3.5 mr-1" />
                    <span>Signature</span>
                  </Button>
                </div>
                
                {/* Row 3: Share */}
                <div>
                  <Button
                    onClick={() => setShowQRModal(true)}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500 text-white py-2.5 rounded-lg text-sm font-semibold"
                    data-testid="share-card-btn"
                  >
                    <Share2 className="w-4 h-4 mr-1.5" />
                    Share Card
                  </Button>
                </div>
              </div>
            ) : user && !contactExists ? (
              // Logged-in user - Contact not added - show Add to Contacts and Download buttons
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleAddContact}
                  variant="outline"
                  className="w-full border-2 border-teal-400 text-teal-600 hover:bg-teal-50 font-semibold py-2.5 sm:py-3 text-xs sm:text-base rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                  <span>Add</span>
                </Button>
                <Button
                  onClick={handleDownloadVCard}
                  variant="outline"
                  className="w-full border-2 border-amber-400 text-amber-600 hover:bg-amber-50 font-semibold py-2.5 sm:py-3 text-xs sm:text-base rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                  <span>Save</span>
                </Button>
              </div>
            ) : user && contactExists ? (
              // Logged-in user - Contact already added - show Share and Download buttons
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => setShowQRModal(true)}
                  variant="outline"
                  className="w-full border-2 border-amber-400 text-amber-600 hover:bg-amber-50 font-semibold py-2.5 sm:py-3 text-xs sm:text-base rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <Share2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                  <span>Share</span>
                </Button>
                <Button
                  onClick={handleDownloadVCard}
                  variant="outline"
                  className="w-full border-2 border-teal-400 text-teal-600 hover:bg-teal-50 font-semibold py-2.5 sm:py-3 text-xs sm:text-base rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                  <span>Save</span>
                </Button>
              </div>
            ) : !user ? (
              // Non-logged-in visitor - show Download Contact and Create Free Card buttons
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleDownloadVCard}
                  variant="outline"
                  className="w-full border-2 border-teal-400 text-teal-600 hover:bg-teal-50 font-semibold py-2.5 sm:py-3 text-xs sm:text-base rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                  <span>Save</span>
                </Button>
                <Button
                  onClick={() => setShowSignInModal(true)}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500 text-white font-semibold py-2.5 sm:py-3 text-xs sm:text-base rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                    <span>Create</span>
                  </Button>
                </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Customize Card Modal - Only for card owner */}
      {user && card.userId === user.uid && (
        <CustomizeCardModal
          open={customizeModalOpen}
          onOpenChange={setCustomizeModalOpen}
          card={card}
          onSave={async (customization) => {
            // Update the card with new customization
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cards/${card._id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await user.getIdToken()}`
              },
              body: JSON.stringify(customization)
            });
            
            if (!response.ok) {
              throw new Error('Failed to update card');
            }
            
            // Reload the card to show updated style
            await loadCard();
          }}
        />
      )}

      {/* Analytics Modal - For card owner or admin */}
      {user && (card.userId === user.uid || isAdmin) && (
        <AnalyticsModal
          isOpen={showAnalyticsModal}
          onClose={() => setShowAnalyticsModal(false)}
          cardId={card._id}
          cardName={card.name}
        />
      )}

      {/* Enhanced QR Code Modal with Share & Offline Contact */}
      <QRPopupEnhanced
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        card={card}
        bioUrl={bioUrl}
        qrColor={(card?.is_premium_card || isSubscribed) ? (card?.qrColor || '#f59e0b') : '#a89743'}
        logoSrc={card?.companyLogo || card?.profileImage || '/logo.png'}
        isPremium={card?.is_premium_card || isSubscribed}
      />

      {/* Email Signature Modal - Clean & Optimized UX */}
      <Dialog open={showEmailSignatureModal} onOpenChange={setShowEmailSignatureModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <Mail className="w-5 h-5 text-amber-500" />
              Create Your Email Signature
            </DialogTitle>
            <p className="text-sm text-gray-500 mt-1">Choose a style and copy to your email settings</p>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            {/* Template Selection - Compact */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {[
                { id: 'classic', name: 'Classic' },
                { id: 'modern', name: 'Modern' },
                { id: 'minimal', name: 'Minimal' },
                { id: 'compact', name: 'Executive' },
                { id: 'banner', name: 'Banner' }
              ].map(template => (
                <button
                  key={template.id}
                  onClick={() => setSelectedSignatureTemplate(template.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedSignatureTemplate === template.id
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {template.name}
                </button>
              ))}
            </div>

            {/* Preview Section - Compact */}
            <div className="bg-gray-50 rounded-xl p-4">
              {isGeneratingQr && (
                <div className="flex items-center justify-center py-4 text-sm text-gray-500">
                  <svg className="animate-spin w-5 h-5 mr-2 text-amber-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating QR code...
                </div>
              )}
              <div className="bg-white border border-gray-200 rounded-lg p-3 inline-block">
                <div 
                  dangerouslySetInnerHTML={{ __html: generateEmailSignatureHTML(selectedSignatureTemplate, signatureQrUrl || card?.signatureQrUrl) }}
                />
              </div>
            </div>

            {/* Copy Button */}
            <Button
              onClick={copyEmailSignature}
              disabled={isGeneratingQr}
              className={`w-full font-semibold py-3.5 text-base rounded-xl ${
                signatureCopied 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-amber-500 hover:bg-amber-600'
              } text-white disabled:opacity-50`}
            >
              {signatureCopied ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Copied! Paste in your email settings
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 mr-2" />
                  Copy Signature
                </>
              )}
            </Button>

            {/* Quick Instructions - Minimal */}
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
              <span><strong>Gmail:</strong> Settings → Signature</span>
              <span className="text-gray-300">|</span>
              <span><strong>Outlook:</strong> Settings → Mail → Signature</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Crop Modal */}
      <Dialog open={showCropModal} onOpenChange={setShowCropModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Crop {cropType === 'profile' ? 'Profile Image' : 'Logo'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative w-full h-80 bg-gray-100 rounded-lg overflow-hidden">
            {cropImageSrc && (
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape={cropType === 'profile' ? 'round' : 'rect'}
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            )}
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Zoom
              </label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleCropCancel}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCropSave}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sign In Modal */}
      <SignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal}
        redirectPath="/create"
      />

      {/* Scan Limit Dialog */}
      <ScanLimitDialog
        open={scanLimitDialog.open}
        onOpenChange={(open) => setScanLimitDialog(prev => ({ ...prev, open }))}
        scansUsed={scanLimitDialog.scansUsed}
        maxScans={scanLimitDialog.maxScans}
        contactEmail={scanLimitDialog.contactEmail}
      />

      {/* Premium Upgrade Modal for Logo Feature */}
      <Dialog open={showPremiumModal} onOpenChange={setShowPremiumModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Crown className="w-6 h-6 text-amber-500" />
              Premium Feature
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                <Lock className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Custom Logo Upload
              </h3>
              <p className="text-gray-600 text-sm">
                Upload your company logo and get it embedded in your QR code. Stand out with a professional branded experience.
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 mb-6 border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src="/piper-logo-qr.png" 
                  alt="Piper Card" 
                  className="w-8 h-8"
                />
                <div>
                  <p className="font-semibold text-gray-900">Premium Logo Subscription</p>
                  <p className="text-sm text-amber-700">Starting at ₹99/month</p>
                </div>
              </div>
              <ul className="text-sm text-gray-700 space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                  Upload custom company logo
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                  Logo embedded in QR code
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                  Priority support
                </li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowPremiumModal(false)}
              >
                Maybe Later
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                onClick={() => {
                  setShowPremiumModal(false);
                  navigate('/pricing');
                }}
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Virtual Background Theme Modal */}
      <Dialog open={showMeetingBgModal} onOpenChange={setShowMeetingBgModal}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg font-semibold">
              <Palette className="w-5 h-5 text-amber-500" />
              Virtual Background Theme
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-4 pt-2">
            {/* Description */}
            <p className="text-xs sm:text-sm text-gray-600">
              Download a professional virtual background with your info for Zoom, Google Meet, or Microsoft Teams.
            </p>

            {/* Category Filter with Upload Button */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 w-full sm:flex-1 scrollbar-hide">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'custom', label: 'My Uploads' },
                  { id: 'professional', label: 'Professional' },
                  { id: 'creative', label: 'Creative' },
                  { id: 'minimal', label: 'Minimal' },
                  { id: 'nature', label: 'Nature' }
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setBgCategory(cat.id)}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-full whitespace-nowrap transition-all ${
                      bgCategory === cat.id
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              {/* Upload Button */}
              <label className="cursor-pointer flex-shrink-0">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadUserBackground(file);
                    e.target.value = '';
                  }}
                />
                <div className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1.5 transition-colors">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Upload
                </div>
              </label>
            </div>

            {/* Background Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 max-h-[250px] sm:max-h-[300px] overflow-y-auto pr-1">
              {meetingBackgrounds
                .filter(bg => bgCategory === 'all' || bg.category === bgCategory || (bgCategory === 'custom' && bg.is_user_uploaded))
                .map((bg) => (
                <div
                  key={bg.id}
                  className="relative group"
                >
                  <button
                    onClick={() => setSelectedBackground(bg)}
                    className={`w-full aspect-video rounded-xl overflow-hidden border-2 transition-all ${
                      selectedBackground?.id === bg.id
                        ? 'border-amber-500 ring-2 ring-amber-500/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                  {bg.type === 'gradient' ? (
                    <div
                      className="w-full h-full"
                      style={{ background: bg.gradient }}
                    />
                  ) : bg.type === 'url' ? (
                    <img
                      src={bg.image_url}
                      alt={bg.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={bg.thumbnail_url?.startsWith('http') ? bg.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${bg.thumbnail_url}`}
                      alt={bg.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  {/* Overlay info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-2">
                    <span className="text-white text-xs font-medium truncate">{bg.name}</span>
                  </div>
                  {bg.is_user_uploaded && (
                    <div className="absolute top-1 left-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                      My Upload
                    </div>
                  )}
                  {bg.is_premium && !(card?.is_premium_card || isSubscribed) && (
                    <div className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <Crown className="w-3 h-3" />
                      Pro
                    </div>
                  )}
                  {selectedBackground?.id === bg.id && !bg.is_user_uploaded && (
                    <div className="absolute top-1 left-1 bg-amber-500 text-white rounded-full p-0.5">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                  </button>
                  {/* Delete button for user uploads */}
                  {bg.is_user_uploaded && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Delete this background?')) {
                          deleteUserBackground(bg.id);
                        }
                      }}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      title="Delete"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Preview */}
            {selectedBackground && (
              <div className="border border-gray-200 rounded-xl p-2 sm:p-3 bg-gray-50">
                <p className="text-[10px] sm:text-xs text-gray-500 mb-2 font-medium">Preview (Card info top-left, QR code top-right)</p>
                <div
                  className="relative aspect-video rounded-lg overflow-hidden"
                  style={selectedBackground.type === 'gradient' 
                    ? { background: selectedBackground.gradient }
                    : {}
                  }
                >
                  {(selectedBackground.type === 'url' || selectedBackground.type === 'image') && (
                    <img
                      src={selectedBackground.type === 'url' ? selectedBackground.image_url : (selectedBackground.thumbnail_url?.startsWith('http') ? selectedBackground.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${selectedBackground.thumbnail_url}`)}
                      alt={selectedBackground.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  {/* Overlay for readability */}
                  <div className="absolute inset-0 bg-black/10" />
                  {/* Top-left: Info card preview */}
                  <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-white/95 rounded-lg p-1.5 sm:p-2 flex items-center gap-1.5 sm:gap-2 shadow-lg max-w-[55%]">
                    {(card?.profileImage || card?.avatar) && (
                      <img
                        src={card?.profileImage || card?.avatar}
                        alt={card?.name}
                        className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0"
                      />
                    )}
                    <div className="min-w-0">
                      <p className="text-[10px] sm:text-xs font-semibold text-gray-900 truncate">{card?.name || 'Your Name'}</p>
                      {card?.title && <p className="text-[8px] sm:text-[10px] text-gray-600 truncate">{card?.title}</p>}
                    </div>
                  </div>
                  {/* Top-right: QR code preview (larger) */}
                  <div className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 bg-white/98 rounded-lg sm:rounded-xl p-1.5 sm:p-2.5 shadow-lg">
                    <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 sm:w-10 sm:h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Download Button */}
            <Button
              onClick={generateMeetingBackground}
              disabled={!selectedBackground || isGeneratingBg}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base"
            >
              {isGeneratingBg ? (
                <>
                  <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Background
                </>
              )}
            </Button>

            {/* Usage tips */}
            <div className="bg-blue-50 rounded-xl p-2.5 sm:p-3 text-[10px] sm:text-xs text-blue-800">
              <p className="font-medium mb-1">How to use:</p>
              <ul className="space-y-0.5 text-blue-700">
                <li>• Zoom: Settings → Background & Effects</li>
                <li>• Meet: More options → Visual effects</li>
                <li>• Teams: Settings → Background effects</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Visitor Registration Modal - Mobile Optimized */}
      <Dialog open={showVisitorModal} onOpenChange={setShowVisitorModal}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md mx-auto p-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto">
          {/* Compact Header */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-4 text-white sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">Welcome!</h2>
                <p className="text-xs text-white/80 truncate">Connect with {card?.name || username}</p>
              </div>
            </div>
          </div>
          
          <div className="px-4 py-4 space-y-4">
            {/* Name Input */}
            <div className="space-y-1.5">
              <Label htmlFor="visitor-name" className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-violet-500" />
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="visitor-name"
                placeholder="Your full name"
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                className="w-full h-11 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                data-testid="visitor-name-input"
              />
            </div>
            
            {/* Phone Input - Stacked on very small screens */}
            <div className="space-y-1.5">
              <Label htmlFor="visitor-phone" className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-violet-500" />
                Phone <span className="text-red-400">*</span>
              </Label>
              <div className="flex gap-2">
                <CountryCodeSelect
                  value={visitorCountryCode}
                  onChange={(e) => setVisitorCountryCode(e.target.value)}
                  className="w-24 h-11 text-sm rounded-xl"
                />
                <Input
                  id="visitor-phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="9876543210"
                  value={visitorPhone}
                  onChange={(e) => setVisitorPhone(e.target.value.replace(/[^\d]/g, ''))}
                  className="flex-1 h-11 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                  data-testid="visitor-phone-input"
                />
              </div>
            </div>
            
            {/* Email Input - Optional */}
            <div className="space-y-1.5">
              <Label htmlFor="visitor-email" className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-violet-500" />
                Email <span className="text-gray-400 font-normal">(Optional)</span>
              </Label>
              <Input
                id="visitor-email"
                type="email"
                placeholder="your@email.com"
                value={visitorEmail}
                onChange={(e) => setVisitorEmail(e.target.value)}
                className="w-full h-11 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                data-testid="visitor-email-input"
              />
            </div>
            
            {/* Address with GPS - Compact */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="visitor-location" className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-violet-500" />
                  Address
                </Label>
                <button
                  type="button"
                  onClick={getGPSLocation}
                  disabled={isLoadingLocation}
                  className="flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-700 active:scale-95 transition-transform disabled:opacity-50"
                  data-testid="gps-location-btn"
                >
                  {isLoadingLocation ? (
                    <>
                      <div className="w-3 h-3 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                      <span>Locating...</span>
                    </>
                  ) : (
                    <>
                      <Navigation className="w-3.5 h-3.5" />
                      <span>Use GPS</span>
                    </>
                  )}
                </button>
              </div>
              
              <div className="relative">
                <Input
                  id="visitor-location"
                  placeholder="Area, City, Country"
                  value={visitorLocation}
                  onChange={(e) => { setVisitorLocation(e.target.value); setVisitorCoords(null); setLocationSource('manual'); }}
                  className="w-full h-11 pr-9 border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl text-sm"
                  data-testid="visitor-location-input"
                />
                {visitorLocation && !isLoadingLocation && (
                  <button
                    type="button"
                    onClick={() => { setVisitorLocation(''); setVisitorCoords(null); setLocationSource(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1.5 rounded-full active:bg-gray-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {locationSource === 'gps' && visitorCoords && (
                <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2.5 py-1 rounded-full w-fit">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  GPS captured
                </div>
              )}
            </div>
            
            {/* Message - Collapsible feel */}
            <div className="space-y-1.5">
              <Label htmlFor="visitor-notes" className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-violet-500" />
                Message <span className="text-gray-400 font-normal">(Optional)</span>
              </Label>
              <textarea
                id="visitor-notes"
                placeholder="Leave a note..."
                value={visitorNotes}
                onChange={(e) => setVisitorNotes(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                rows={2}
                maxLength={500}
                data-testid="visitor-notes-input"
              />
            </div>
            
            {/* Action Buttons - Full width, stacked feel */}
            <div className="flex gap-2 pt-1 pb-1">
              <Button
                variant="outline"
                onClick={skipVisitorRegistration}
                className="flex-1 h-11 text-sm text-gray-500 border-gray-200 hover:bg-gray-50 rounded-xl active:scale-[0.98] transition-transform"
                data-testid="skip-visitor-btn"
              >
                Skip
              </Button>
              <Button
                onClick={submitVisitorRegistration}
                disabled={isSubmittingVisitor || !visitorName || !visitorPhone}
                className="flex-[2] h-11 text-sm bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg shadow-violet-500/20 active:scale-[0.98] transition-transform disabled:opacity-50"
                data-testid="submit-visitor-btn"
              >
                {isSubmittingVisitor ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1.5" />
                    Connect
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Book Demo Modal - For visitors */}
      <BookDemoModal
        open={showDemoModal}
        onOpenChange={setShowDemoModal}
        cardUsername={username}
        cardOwnerName={card?.name}
      />

      {/* Delete Card Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={(open) => {
        setShowDeleteModal(open);
        if (!open) {
          setDeleteConfirmText('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Your Card
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Warning Message */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-800 font-medium mb-2">
                ⚠️ This action cannot be undone!
              </p>
              <p className="text-red-700 text-sm">
                Once deleted, anyone who tries to reach you using your bio link or QR code will not be able to access your card anymore.
              </p>
            </div>
            
            {/* Card Info */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-1">Card to be deleted:</p>
              <p className="font-medium text-gray-900">{card?.name}</p>
              <p className="text-sm text-gray-500">pipercard.in/bio/{username}</p>
            </div>
            
            {/* Confirmation Input */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Type <span className="font-bold text-red-600">delete</span> to confirm:
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'delete' here"
                className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                data-testid="delete-confirm-input"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                className="flex-1"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteCard}
                disabled={deleteConfirmText.toLowerCase() !== 'delete' || isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-delete-btn"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Card
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Bio;
